import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";

import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";

import DefaultAuth from "layouts/auth/Default";
import illustration from "assets/img/auth/auth.png";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { RiEyeCloseLine } from "react-icons/ri";
import config from "config";

function ResetPasswordConfirm() {
  const navigate = useNavigate();
  const { token } = useParams();

  const textColor = useColorModeValue("navy.700", "white");
  const textColorSecondary = "gray.400";
  const brandStars = useColorModeValue("brand.500", "brand.400");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  
  const [show, setShow] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleClick = () => setShow(!show);
  const handleConfirmClick = () => setShowConfirm(!showConfirm);

  const handleReset = async () => {
    if (!token) {
      return toast.error("Invalid or missing reset token");
    }

    if (password.length < 6) {
      return toast.error("Password must be at least 6 characters long");
    }

    if (password !== confirmPassword) {
      return toast.error("Passwords do not match");
    }

    setLoading(true);

    const resetPromise = fetch(
      `${config.BACKEND_API_URL}/auth/reset-password/${token}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ password })
      }
    ).then(async (res) => {
      let data = {};
      try {
        data = await res.json();
      } catch {}

      if (!res.ok || !data.success) {
        throw new Error(data.message || "Invalid or expired link");
      }

      return data;
    });

    toast.promise(resetPromise, {
      loading: "Resetting password...",
      success: "Your password has been successfully reset.",
      error: (err) => err.message || "Failed to reset password",
    });

    try {
      await resetPromise;
      setTimeout(() => {
        navigate("/auth/sign-in");
      }, 1500);
    } catch {}

    setLoading(false);
  };

  const inputStyles = {
    background: 'var(--input-bg)',
    border: '1px solid var(--border)',
    color: 'var(--text-primary)'
  };

  return (
    <DefaultAuth illustrationBackground={illustration} image={illustration}>
      <Flex
        maxW={{ base: "100%", md: "max-content" }}
        w='100%'
        mx={{ base: "auto", lg: "0px" }}
        me='auto'
        h='100%'
        alignItems='start'
        justifyContent='center'
        mb={{ base: "30px", md: "60px" }}
        px={{ base: "25px", md: "0px" }}
        mt={{ base: "40px", md: "14vh" }}
        flexDirection='column'>

        <Box me='auto'>
          <Heading color={textColor} fontSize='36px' mb='10px'>
            Reset Your Password
          </Heading>
          <Text
            mb='36px'
            ms='4px'
            color={textColorSecondary}
            fontWeight='400'
            fontSize='md'>
            Enter your new password below
          </Text>
        </Box>

        <Flex
          zIndex='2'
          direction='column'
          w={{ base: "100%", md: "420px" }}
          maxW='100%'
          background='transparent'
          borderRadius='15px'
          mx={{ base: "auto", lg: "unset" }}
          me='auto'
          mb={{ base: "20px", md: "auto" }}>

          <FormControl>
            <FormLabel ms='4px' fontSize='sm' fontWeight='500' color={textColor} display='flex'>
              New Password<Text color={brandStars}>*</Text>
            </FormLabel>

            <InputGroup size='md'>
              <Input
                placeholder='Min. 6 characters'
                mb='24px'
                size='lg'
                type={show ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={inputStyles}
              />
              <InputRightElement mt='4px'>
                <Icon
                  color={textColorSecondary}
                  cursor='pointer'
                  as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                  onClick={handleClick}
                />
              </InputRightElement>
            </InputGroup>

            <FormLabel ms='4px' fontSize='sm' fontWeight='500' color={textColor} display='flex'>
              Confirm Password<Text color={brandStars}>*</Text>
            </FormLabel>

            <InputGroup size='md'>
              <Input
                placeholder='Confirm new password'
                mb='24px'
                size='lg'
                type={showConfirm ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                style={inputStyles}
              />
              <InputRightElement mt='4px'>
                <Icon
                  color={textColorSecondary}
                  cursor='pointer'
                  as={showConfirm ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                  onClick={handleConfirmClick}
                />
              </InputRightElement>
            </InputGroup>

            <Button
              onClick={handleReset}
              isLoading={loading}
              loadingText="Resetting..."
              fontSize='sm'
              className='primary-btn'
              fontWeight='500'
              w='100%'
              h='50'
              mb='24px'
              style={{ background: '#6C63FF', color: 'white' }}>
              Reset Password
            </Button>
          </FormControl>
        </Flex>
      </Flex>
    </DefaultAuth>
  );
}

export default ResetPasswordConfirm;
